/**
 * Stepper — −/+-kontroll for et tall (#152, mangler i MAPPING.md §3).
 *
 * Erstatter tallfeltene i ProposeChoreSheet og ProposalCard når de
 * skjermene migreres. (Poengjusteringen på Familie bruker hurtigchips og et
 * tallfelt, som skissen, #159.) To runde sekundærknapper
 * rundt verdien, som i Foreslå-skissen:
 *   - lg  52 px knapper og 30 px tall (Foreslå oppgave)
 *   - md  44 px knapper og 15 px tall (rader, f.eks. Godkjenn). Skissen har
 *         32 px her, men målene skal være ≥ 44 px.
 *
 * Verdien er `aria-live="polite"`, så skjermlesere hører den nye verdien.
 * Knappene deaktiveres ved min/maks i stedet for å gjøre ingenting.
 * Gruppenavnet er `label`; knappenavnene er «Færre»/«Flere» med mindre
 * kallstedet gir mer presise («Færre poeng»).
 */
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from './Icon';

type StepperSize = 'md' | 'lg';

const BUTTON_SIZE: Record<StepperSize, string> = {
  md: 'h-11 w-11',
  lg: 'h-13 w-13',
};

const VALUE_CLASS: Record<StepperSize, string> = {
  md: 'min-w-10 text-body-s',
  lg: 'min-w-19 text-3xl tracking-tight',
};

interface StepperProps {
  value: number;
  onChange: (next: number) => void;
  label: string;
  min?: number;
  max?: number;
  step?: number;
  size?: StepperSize;
  /** Vises etter tallet, f.eks. « p». */
  suffix?: string;
  decreaseLabel?: string;
  increaseLabel?: string;
  className?: string;
  'data-testid'?: string;
}

export function Stepper({
  value,
  onChange,
  label,
  min = Number.NEGATIVE_INFINITY,
  max = Number.POSITIVE_INFINITY,
  step = 1,
  size = 'lg',
  suffix,
  decreaseLabel,
  increaseLabel,
  className,
  'data-testid': testId = 'stepper',
}: StepperProps): ReactNode {
  const { t } = useTranslation();
  const atMin = value - step < min;
  const atMax = value + step > max;
  const button = [
    'inline-flex shrink-0 items-center justify-center rounded-full border border-secondary-line bg-secondary text-ink',
    'disabled:opacity-40',
    BUTTON_SIZE[size],
  ].join(' ');
  const glyph = size === 'lg' ? 22 : 18;

  return (
    <div
      role="group"
      aria-label={label}
      data-testid={testId}
      className={['inline-flex items-center', size === 'lg' ? 'gap-3.5' : 'gap-1', className]
        .filter(Boolean)
        .join(' ')}
    >
      <button
        type="button"
        data-testid={`${testId}-decrease`}
        aria-label={decreaseLabel ?? t('stepper.decrease')}
        disabled={atMin}
        onClick={() => onChange(Math.max(min, value - step))}
        className={button}
      >
        <Icon.Minus size={glyph} />
      </button>
      <span
        data-testid={`${testId}-value`}
        aria-live="polite"
        className={['text-center font-extrabold text-ink tnum', VALUE_CLASS[size]].join(' ')}
      >
        {value}
        {suffix}
      </span>
      <button
        type="button"
        data-testid={`${testId}-increase`}
        aria-label={increaseLabel ?? t('stepper.increase')}
        disabled={atMax}
        onClick={() => onChange(Math.min(max, value + step))}
        className={button}
      >
        <Icon.Plus size={glyph} />
      </button>
    </div>
  );
}
